// Real plan tiers. `id` matches the Convex `tenants.plan` union
// ("starter" | "pro" | "enterprise") so the marketing calculator and the
// create-tenant mutation speak the same language. Limits gate migration volume
// (spec §7). Prices are one-time migration fees (Stripe sandbox wires them for
// real in P1c/Phase 2). Honest copy only — no fabricated guarantees.

export type PlanId = "starter" | "pro" | "enterprise";

export interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  /** One-time migration price in USD, or null for custom/contact-us. */
  priceUsd: number | null;
  /** Inclusive upper bounds; null = no fixed cap (custom scoping). */
  limits: {
    messages: number | null;
    members: number | null;
    channels: number | null;
  };
  features: string[];
}

export const PLANS: readonly Plan[] = [
  {
    id: "starter",
    name: "Starter",
    tagline: "A small team with a few years of history.",
    priceUsd: 99,
    limits: { messages: 50_000, members: 25, channels: 10 },
    features: [
      "Up to 50,000 messages",
      "Up to 25 members, 10 channels",
      "Threads, reactions & files preserved",
      "Live web progress dashboard",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "A growing org consolidating onto Discord.",
    priceUsd: 399,
    limits: { messages: 500_000, members: 100, channels: 50 },
    features: [
      "Up to 500,000 messages",
      "Up to 100 members, 50 channels",
      "7-day live mirror during cutover",
      "Live Discord scoreboard embed",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "A large org with a decade of messages.",
    priceUsd: null,
    limits: { messages: null, members: null, channels: null },
    features: [
      "Unlimited messages & channels",
      "Priority migration queue",
      "Large media handled at any scale",
      "Dedicated migration support",
    ],
  },
];

export function getPlan(id: PlanId): Plan {
  const plan = PLANS.find((p) => p.id === id);
  if (!plan) throw new Error(`Unknown plan: ${id}`);
  return plan;
}

export interface Workload {
  messages: number;
  members: number;
  channels: number;
}

/**
 * Recommend the smallest plan whose limits cover the workload. A `null` limit
 * means "no cap", so Enterprise always fits and is the fallback for anything
 * that overflows Starter/Pro.
 */
export function recommendPlan(w: Workload): PlanId {
  const fits = (limit: number | null, value: number) =>
    limit === null || value <= limit;
  for (const plan of PLANS) {
    if (
      fits(plan.limits.messages, w.messages) &&
      fits(plan.limits.members, w.members) &&
      fits(plan.limits.channels, w.channels)
    ) {
      return plan.id;
    }
  }
  return "enterprise";
}

/** Compact USD formatting for large message counts, e.g. 50000 -> "50K". */
export function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return `${n}`;
}
